import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiDeviation, PapiTag } from '@wix/da-papi-types';
import { isSSR } from '@wix/da-ddt/lib/src/server';
import { BiData, DeviationEditClickBiEvent, TagClickBiEvent } from '@wix/da-bi/pkg/events';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import PlaceholderBlock from '@wix/da-shared-react/pkg/PlaceholderBlock';
import TagList from '@wix/da-ds/pkg/TagList';
import { TagType } from '@wix/da-ds/pkg/TagList/types';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { useEditDeviationClick } from '../../../../hooks/useEditDeviationClick';
import { measureVisibleTags } from './utils';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';

import s from './DeviationTags.scss';

export interface Props {
  deviation: PapiDeviation;
  tags: PapiTag[];
  columnWidth?: number;
  collapsed?: boolean;
  className?: string;
}

const DeviationTags: FC<Props> = ({ deviation, tags, columnWidth, collapsed, className }) => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { onEditClick } = useEditDeviationClick(deviation);
  const [visibleTags, setVisibleTags] = useState<PapiTag[]>([]);
  const [hiddenCount, setHiddenCount] = useState(0);

  const hasTags = useMemo(() => tags.length > 0, [tags.length]);
  const hasHiddenTags = useMemo(() => hiddenCount > 0, [hiddenCount]);

  useEffect(() => {
    if (collapsed || !columnWidth) {
      return;
    }

    const { visible, hidden } = measureVisibleTags(tags, columnWidth);
    setVisibleTags(visible);
    setHiddenCount(hidden);
  }, [tags, collapsed, columnWidth]);

  const mapTagToItem = useCallback(
    ({ name, url }: PapiTag) => ({
      tagname: name,
      href: url,
      className: cn(s['tag-button'], s['tag']),
      biData: BiData<TagClickBiEvent>({
        evid: 391,
        tag_text: name,
        link_url: url,
        typeid: getDeviationResourceType(deviation),
        itemid: deviation.deviationId,
      }),
    }),
    [deviation]
  );

  const visibleTagItems: TagType[] = useMemo(
    () => visibleTags.map(mapTagToItem),
    [visibleTags, mapTagToItem]
  );

  const hiddenTagItems: TagType[] = useMemo(
    () => tags.slice(visibleTags.length).map(mapTagToItem),
    [tags, visibleTags, mapTagToItem]
  );

  if (isSSR() || !isMounted) {
    return (
      <div className={cn(s['root'], s['ssr'])}>
        {!hasTags && <PlaceholderBlock className={s['placeholder-block']} width={87} />}
        {hasTags &&
          tags.slice(0, 3).map(({ name }) => (
            <PlaceholderBlock key={`tag-${name}`} className={s['placeholder-block']}>
              {name}
            </PlaceholderBlock>
          ))}
      </div>
    );
  }

  const editClickBi = BiData<DeviationEditClickBiEvent>({
    evid: 371,
    itemid: deviation.deviationId,
    typeid: getDeviationResourceType(deviation),
    sectionname: 'tags_btn',
  });

  const tagsTooltipWithButton = (button: ReactNode) => (
    <SimpleTooltip
      contentClassName={s['tooltip']}
      text={
        <TagList className={s['tooltip-tag-list']} size="small" tags={hiddenTagItems} multiline />
      }
      usePortal
    >
      {button}
    </SimpleTooltip>
  );

  const singleTagButton =
    collapsed || !hasTags ? (
      <TagList
        size="small"
        tags={[
          {
            tagname: t('studio.table.tags.label', { count: tags.length }),
            biData: editClickBi,
            className: s['tag-button'],
            onClick: onEditClick,
          },
        ]}
      />
    ) : null;

  return (
    <div className={cn(s['root'], className)}>
      {collapsed && hasTags && tagsTooltipWithButton(singleTagButton)}
      {collapsed && !hasTags && singleTagButton}

      {!collapsed && hasTags && <TagList size="small" tags={visibleTagItems} />}
      {!collapsed && !hasTags && singleTagButton}

      {hasHiddenTags &&
        tagsTooltipWithButton(
          <TagList
            size="small"
            tags={[
              {
                tagname: `+${hiddenCount}`,
                biData: editClickBi,
                className: s['tag-button'],
                onClick: onEditClick,
              },
            ]}
          />
        )}
    </div>
  );
};
DeviationTags.displayName = 'DeviationTags';

export default DeviationTags;
